import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import moment from "moment-timezone";
import { useUsersContext } from "../../../context/usersContext";
import { useUserInfo } from "../../../context/user";
import { useRunTaskMutation } from "../../../queries/tasks";

const useRunTask = () => {
  const { createConvesation, fetchChats } = useUsersContext();
  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const { mutateAsync: runTaskRequest } = useRunTaskMutation();
  const [running, setRunning] = useState(false);


  const runTask = useCallback(
    async (task) => {
      const taskOutput = (task?.output || "chatUi").toString().toLowerCase();
      const taskPrompt = task?.prompt_value || "";

      // Resolves true only when the run actually went through, so callers can
      // tell a real run from a swallowed failure.
      if (!taskPrompt) {
        toast.error("This task does not have prompt content to run.");
        return false;
      }

      setRunning(true);
      try {
        if (taskOutput === "chatui") {
          await createConvesation({
            title: task?.title,
            message: taskPrompt,
            messagetype: "notimage",
            addInMessages: true,
            assistant_model_name: "openai/gpt-4o",
          });
          return true;
        }

        const timeZone = moment.tz.guess() || "UTC";

        // Timeout (30 min) and retry:false live on useRunTaskMutation in
        // src/queries/tasks.js — must cover the server chain's 30-minute
        // budget: a shorter client timeout abandons a run that is still
        // executing, and a retry then delivers the task to Slack twice.
        const response = await runTaskRequest({
          profile_id: userActiveLocation?.id,
          user_id: userInfo?.userId,
          provider_id: task?.output,
          message: taskPrompt,
          channel_id: task?.channel,
          ...(task?.integration_id != null
            ? { integration_id: task.integration_id }
            : {}),
          title: task?.title,
          timezone: timeZone,
        });

        if (response?.data?.message) {
          toast.success(response.data.message);
        }

        await fetchChats();
        return true;
      } catch (error) {
        console.error("Error running task:", error);
        toast.error(
          error?.response?.data?.message ||
          "Something went wrong while running the task.",
        );
        return false;
      } finally {
        setRunning(false);
      }
    },
    [
      runTaskRequest,
      createConvesation,
      fetchChats,
      userActiveLocation?.agency_id,
      userActiveLocation?.id,
      userInfo?.userId,
    ],
  );

  return { runTask, running };
};

export default useRunTask;
